import { useFormik } from "formik";
import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "store/hooks";
import { clearErrors, loginRequest } from "store/slices/authSlice";

import useValidation from "hooks/useValidation";
import { PAGES } from "utils/links";

import PasswordInput from "components/PasswordInput";
import TextInput from "components/TextInput";

import { validationSchema } from "./validationSchema";

import Button from "components/Button";
import Checkbox from "components/Checkbox";
import LoginTemplate from "modules/login/LoginTemplate";
import styles from "./style.module.scss";

const LoginPage = () => {
  const dispatch = useAppDispatch();
  const { errors: authErrors, isLoading } = useAppSelector((state) => state.auth);
  const formRef = useRef<HTMLFormElement>(null);

  const form = useFormik({
    initialValues: {
      email: "",
      password: "",
      remember: true,
    },
    onSubmit: (values) => {
      dispatch(
        loginRequest({
          email: values.email,
          password: values.password,
          remember_me: values.remember,
        }),
      );
    },
    validationSchema: validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
  });

  const handleChangeRemember = (checked: boolean) => {
    form.setFieldValue("remember", checked);
  };

  // Clear API errors when user changes form fields
  useEffect(() => {
    if (authErrors) {
      dispatch(clearErrors());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.email, form.values.password]);

  useValidation(formRef, form.errors);

  return (
    <LoginTemplate title="Welcome back" description={`Please sign-in to your account\n and start the work`}>
      <form ref={formRef} onSubmit={form.handleSubmit} className={styles.form}>
        <TextInput
          name="email"
          label="Email address"
          placeholder="your.email@company.com"
          value={form.values.email}
          onChange={form.handleChange}
          error={form.errors.email}
        />

        <PasswordInput
          name="password"
          label="Password"
          placeholder="Enter your password"
          value={form.values.password}
          onChange={form.handleChange}
          error={form.errors.password}
        />

        {typeof authErrors === "string" && <p className={styles.apiError}>{authErrors}</p>}

        <div className={styles.forgotPassword}>
          <div className={styles.rememberMe}>
            <Checkbox name="remember" checked={form.values.remember} onChange={handleChangeRemember} />
            <label htmlFor="remember">Remember me</label>
          </div>

          <Link className={styles.forgotPasswordLink} to={PAGES.RESET_PASSWORD}>
            Forgot password?
          </Link>
        </div>

        <Button className={styles.button} variant="primary" disabled={isLoading}>
          {isLoading ? "Signing in..." : "Sign in"}
        </Button>
      </form>
    </LoginTemplate>
  );
};

export default LoginPage;
